import {
  EllipsisOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MenuOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Collapse, Dropdown, Menu, Modal, PageHeader, Space, Spin, Tooltip, Typography } from 'antd';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import React, { useCallback, useEffect, useState } from 'react';
import { Field, FieldGroup, Grid } from '../../../models';
import { apiClient, apiErrorMessage, getDataTypeName, parseHyperlink } from '../../../utils';
import { EditFieldPage } from './edit-field-page';
import './fields-page.scoped.scss';
import { EntityChangeLog } from '../../reports';
import { EditFieldGroupPage } from './edit-field-group-page';
import arrayMove from 'array-move';

export function FieldsPage(props: { grid?: Grid; gridLoading?: boolean; onSaved?: (grid: Grid) => void }) {
  const { Text } = Typography;

  const [addFieldModalOpen, setAddFieldModalOpen] = useState<{ isOpen: boolean; level?: string[] }>();
  const [editFieldModalOpen, setEditModalOpen] = useState<{ field: Field; level?: string[] }>();
  const [addFieldGroupModalOpen, setAddFieldGroupModalOpen] = useState<{ isOpen: boolean; level?: string[] }>();
  const [editFieldGroupModalOpen, setEditFieldGroupModalOpen] = useState<{ fieldGroup: FieldGroup; level?: string[] }>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<{ entityType: string; recordId: string }>();
  const [loading, setLoading] = useState(false);
  const [grid, setGrid] = useState<Grid>();
  const [scheme, setScheme] = useState<any[]>();
  const [gridLoading, setGridLoading] = useState(false);

  useEffect(() => {
    setGrid(props.grid);
  }, [props.grid]);

  useEffect(() => {
    setGridLoading(props.gridLoading || false);
  }, [props.gridLoading]);

  useEffect(() => {
    if (scheme && !props.grid?.scheme) {
      return;
    }
    if (props.grid?.scheme) {
      setScheme(props.grid.scheme);
    } else {
      const newScheme: any[] = [];
      if (props.grid?.fields?.length) {
        for (const field of props.grid?.fields) {
          newScheme.push({ itemType: 'field', id: field.id });
        }
      }
      if (props.grid?.fieldGroups?.length) {
        for (const fieldGroup of props.grid?.fieldGroups) {
          newScheme.push({ itemType: 'fieldGroup', id: fieldGroup.id });
        }
      }
      setScheme(props.grid?.scheme ?? newScheme);
    }
  }, [props.grid?.fieldGroups, props.grid?.fields, props.grid?.scheme, scheme, props.grid]);

  const loadGrid = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`grids/${props.grid?.id}`)
      .then((response: any) => {
        setGrid(response.data);
        props.onSaved?.(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props]);

  const openAddFieldModal = (level?: string[]) => {
    setAddFieldModalOpen({ isOpen: true, level });
  };

  const closeAddFieldModal = () => {
    setAddFieldModalOpen(undefined);
  };

  const openEditFieldModal = (field: Field, level?: string[]) => {
    setEditModalOpen({ field, level });
  };

  const closeEditFieldModal = () => {
    setEditModalOpen(undefined);
  };

  const openAddFieldGroupModal = (level?: string[]) => {
    setAddFieldGroupModalOpen({ isOpen: true, level });
  };

  const closeAddFieldGroupModal = () => {
    setAddFieldGroupModalOpen(undefined);
  };

  const openEditFieldGroupModal = (fieldGroup: FieldGroup, level?: string[]) => {
    setEditFieldGroupModalOpen({ fieldGroup, level });
  };

  const closeEditFieldGroupModal = () => {
    setEditFieldGroupModalOpen(undefined);
  };

  const openChangeLogModal = (entityType: string, recordId: string) => {
    setChangeLogModalOpen({ entityType, recordId });
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadGrid();
  };

  const onFieldChanged = () => {
    closeAddFieldModal();
    closeEditFieldModal();
    loadGrid();
  };

  const onFieldGroupChanged = () => {
    closeAddFieldModal();
    closeEditFieldModal();
    loadGrid();
  };

  const deleteField = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`fields/${id}`)
      .then(() => {
        loadGrid();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const deleteFieldGroup = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`field-groups/${id}`)
      .then(() => {
        loadGrid();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteField = (id: string) => {
    const onOk = () => deleteField(id);
    Modal.confirm({
      title: 'Veri elemanı sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Veri elemanını silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const confirmDeleteFieldGroup = (id: string) => {
    const onOk = () => deleteFieldGroup(id);
    Modal.confirm({
      title: 'Veri elemanı grubu sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Veri elemanını grubunu silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const updateGridScheme = (level: string[], oldIndex: number, newIndex: number) => {
    setLoading(true);
    const body = { level, oldIndex, newIndex };
    apiClient
      .put(`grids/${props.grid?.id}/scheme`, body)
      .then(() => {
        loadGrid();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const onFieldNameClick = (field: Field, level?: string[]) => {
    openEditFieldModal(field, level);
  };

  const onFieldGroupNameClick = (fieldGroup: FieldGroup, level?: string[]) => {
    openEditFieldGroupModal(fieldGroup, level);
  };

  const onFieldActionClick = (e: any, id: string, level: string[]) => {
    if (e.key === 'edit') {
      const field = grid?.fields?.find(m => m.id === id);
      openEditFieldModal(field as Field, level);
    } else if (e.key === 'changelog') {
      openChangeLogModal('Field', id);
    } else if (e.key === 'del') {
      confirmDeleteField(id);
    }
  };

  const onFieldGroupActionClick = (e: any, id: string, level: string[]) => {
    if (e.key === 'edit') {
      const fieldGroup = grid?.fieldGroups?.find(m => m.id === id);
      openEditFieldGroupModal(fieldGroup as FieldGroup, level);
    } else if (e.key === 'addField') {
      openAddFieldModal(level);
    } else if (e.key === 'addFieldGroup') {
      openAddFieldGroupModal(level);
    } else if (e.key === 'changelog') {
      openChangeLogModal('Field', id);
    } else if (e.key === 'del') {
      confirmDeleteFieldGroup(id);
    }
  };

  const fieldTableActions = (id: string, level: string[]) => (
    <Menu onClick={(e: any) => onFieldActionClick(e, id, level)}>
      <Menu.Item key="edit">Düzenle</Menu.Item>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const fieldGroupTableActions = (id: string, level: string[]) => (
    <Menu onClick={(e: any) => onFieldGroupActionClick(e, id, level)}>
      <Menu.Item key="edit">Düzenle</Menu.Item>
      <Menu.Item key="addField">Yeni veri elemanı</Menu.Item>
      <Menu.Item key="addFieldGroup">Yeni veri elemanı grubu</Menu.Item>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const onSortEnd = (level: string[], { oldIndex, newIndex }: any) => {
    if (oldIndex === newIndex) {
      return;
    }
    let newScheme = JSON.parse(JSON.stringify(scheme)) as any[];
    let newSubScheme = newScheme;
    if (level?.length) {
      for (const levelItem of level) {
        const updatedNode = newSubScheme.find(s => s.id === levelItem) as any;
        if (!updatedNode.children) {
          updatedNode.children = [];
        }
        newSubScheme = updatedNode.children as any[];
      }
    }
    const newOrderedSubScheme = arrayMove([].concat(newSubScheme as never[]), oldIndex, newIndex).filter(el => !!el);
    newSubScheme.push(...newOrderedSubScheme);
    newSubScheme = newSubScheme.splice(0, newOrderedSubScheme.length);
    setScheme(newScheme);
    updateGridScheme(level, oldIndex, newIndex);
  };

  return (
    <>
      <PageHeader
        style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}
        extra={[
          <Text key="fieldCount">
            <b>Veri elemanı sayısı:</b> {grid?.fields?.length || 0}
          </Text>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="addField" onClick={() => openAddFieldModal()} icon={<PlusOutlined />} type="primary">
            Yeni veri elemanı
          </Button>,
          <Button key="addFieldGroup" onClick={() => openAddFieldGroupModal()} icon={<PlusOutlined />} type="primary">
            Yeni veri elemanı grubu
          </Button>,
        ]}
      />
      <Spin spinning={loading || gridLoading}>
        <ItemsPanel
          grid={grid}
          level={[]}
          panelScheme={scheme}
          onFieldNameClick={onFieldNameClick}
          onFieldGroupNameClick={onFieldGroupNameClick}
          onSortEnd={onSortEnd}
          fieldTableActions={fieldTableActions}
          fieldGroupTableActions={fieldGroupTableActions}
        />
      </Spin>
      <Modal
        title="Yeni veri elemanı"
        visible={addFieldModalOpen?.isOpen}
        width="50%"
        footer={null}
        onCancel={closeAddFieldModal}
        destroyOnClose={true}
      >
        <EditFieldPage
          gridId={grid?.id}
          fieldGroups={props.grid?.fieldGroups}
          fields={grid?.fields}
          scheme={scheme}
          level={addFieldModalOpen?.level}
          onSaved={onFieldChanged}
        />
      </Modal>
      <Modal
        title="Veri elemanı düzenle"
        visible={editFieldModalOpen !== undefined}
        width="50%"
        footer={null}
        onCancel={closeEditFieldModal}
        destroyOnClose={true}
      >
        <EditFieldPage
          gridId={props.grid?.id}
          fieldGroups={props.grid?.fieldGroups}
          fields={grid?.fields}
          scheme={scheme}
          level={editFieldModalOpen?.level}
          field={editFieldModalOpen?.field}
          onSaved={onFieldChanged}
        />
      </Modal>
      <Modal
        title="Yeni veri elemanı grubu"
        visible={addFieldGroupModalOpen?.isOpen}
        width="50%"
        footer={null}
        onCancel={closeAddFieldGroupModal}
        destroyOnClose={true}
      >
        <EditFieldGroupPage
          gridId={props.grid?.id}
          fieldGroups={props.grid?.fieldGroups}
          scheme={scheme}
          level={addFieldGroupModalOpen?.level}
          onSaved={onFieldGroupChanged}
        />
      </Modal>
      <Modal
        title="Veri elemanı grubu düzenle"
        visible={editFieldGroupModalOpen !== undefined}
        footer={null}
        onCancel={closeEditFieldGroupModal}
        destroyOnClose={true}
        width={'50%'}
      >
        <EditFieldGroupPage
          gridId={props.grid?.id}
          fieldGroups={props.grid?.fieldGroups}
          scheme={scheme}
          level={editFieldGroupModalOpen?.level}
          fieldGroup={editFieldGroupModalOpen?.fieldGroup}
          onSaved={onFieldGroupChanged}
        />
      </Modal>
      <Modal
        title="Veri elemanı değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={changeLogModalOpen?.entityType || ''} recordId={changeLogModalOpen?.recordId} />
      </Modal>
    </>
  );
}

function ItemsPanel(props: {
  grid?: Grid;
  level: string[];
  panelScheme?: any[];
  fieldTableActions: any;
  fieldGroupTableActions: any;
  onFieldNameClick: (field: Field, level?: string[]) => void;
  onFieldGroupNameClick: (fieldGroup: FieldGroup, level?: string[]) => void;
  onSortEnd: (level: string[], { oldIndex, newIndex }: any) => void;
}) {
  const [fields, setFields] = useState<Field[]>();
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>();
  const [panelScheme, setPanelScheme] = useState<any[]>();

  useEffect(() => {
    setFields(props.grid?.fields);
  }, [props.grid?.fields]);

  useEffect(() => {
    setFieldGroups(props.grid?.fieldGroups);
  }, [props.grid?.fieldGroups]);

  useEffect(() => {
    setPanelScheme(props.panelScheme);
  }, [props.panelScheme]);

  const SortableItem = SortableElement(({ item }: any) => {
    if (item.itemType === 'field') {
      const field = fields?.find(f => f.id === item.id);
      if (field) {
        return FieldTr({ field, level: props.level, onFieldNameClick: props.onFieldNameClick, tableActions: props.fieldTableActions });
      }
    } else if (item.itemType === 'fieldGroup') {
      const fieldGroup = fieldGroups?.find(g => g.id === item.id);
      if (fieldGroup) {
        return FieldGroupTr({
          grid: props.grid,
          fieldGroup,
          level: props.level,
          panelScheme: panelScheme?.find(s => s.id === fieldGroup.id)?.children as any[],
          fieldTableActions: props.fieldTableActions,
          fieldGroupTableActions: props.fieldGroupTableActions,
          onFieldNameClick: props.onFieldNameClick,
          onFieldGroupNameClick: props.onFieldGroupNameClick,
          onSortEnd: props.onSortEnd,
        });
      }
    }
    return null;
  });

  const SortableList = SortableContainer(({ scheme }: any) => {
    return (
      <>
        <table key={props.level.join('-')}>
          <tbody>
            {scheme
              ?.filter((item: any) => {
                if (item.itemType === 'field' && fields?.some(f => f.id === item.id)) {
                  return true;
                }
                if (item.itemType === 'fieldGroup' && fieldGroups?.some(fg => fg.id === item.id)) {
                  return true;
                }
                return false;
              })
              .map((item: any, i: number) => {
                return <SortableItem key={`item-${item.id}`} index={i} item={item} />;
              })}
          </tbody>
        </table>
      </>
    );
  });

  const onSortEnd = ({ oldIndex, newIndex }: any) => {
    props.onSortEnd(props.level || [], { oldIndex, newIndex });
  };

  return <SortableList scheme={panelScheme} useDragHandle disableAutoscroll helperClass="row-dragging" onSortEnd={onSortEnd} />;
}

function FieldGroupTr(props: {
  grid?: Grid;
  fieldGroup: FieldGroup;
  level: string[];
  panelScheme?: any[];
  fieldTableActions: any;
  fieldGroupTableActions: any;
  onFieldGroupNameClick: (fieldGroup: FieldGroup, level?: string[]) => void;
  onFieldNameClick: (field: Field, level?: string[]) => void;
  onSortEnd: (level: string[], { oldIndex, newIndex }: any) => void;
}) {
  const [isActive, setIsActive] = useState(true);
  const [level, setLevel] = useState<string[]>([]);
  const [panelScheme, setPanelScheme] = useState<any[]>();

  const { Text } = Typography;

  useEffect(() => {
    setPanelScheme(props.panelScheme);
  }, [props.panelScheme]);

  useEffect(() => {
    setLevel(props.level);
  }, [props.level]);

  const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);

  const onChange = () => {
    setIsActive(!isActive);
  };

  return (
    <tr>
      <td colSpan={2} style={{ width: '10000rem' }}>
        <Collapse
          activeKey={isActive ? props.fieldGroup.id : undefined}
          onChange={onChange}
          style={{ borderColor: props.fieldGroup.hexCodeBackground }}
        >
          <Collapse.Panel
            style={{ backgroundColor: props.fieldGroup.hexCodeBackground, borderColor: props.fieldGroup.hexCodeBackground }}
            header={
              <Space>
                <Button
                  type="link"
                  style={{ padding: 0, height: 'auto', border: 0, fontWeight: 700, color: props.fieldGroup.hexCodeTitle }}
                  onClick={() => props.onFieldGroupNameClick(props.fieldGroup, props.level)}
                >
                  {props.fieldGroup.name}
                </Button>
                <Tooltip title={props.fieldGroup.description}>
                  <Text type="secondary">
                    <InfoCircleOutlined />
                  </Text>
                </Tooltip>
              </Space>
            }
            key={props.fieldGroup.id}
            extra={
              <Space>
                <Dropdown overlay={props.fieldGroupTableActions(props.fieldGroup.id, props.level)} trigger={['click']}>
                  <Button size="small" icon={<EllipsisOutlined />} />
                </Dropdown>
                <DragHandle />
              </Space>
            }
          >
            <>
              <ItemsPanel
                grid={props.grid}
                level={[...level, props.fieldGroup.id]}
                panelScheme={panelScheme}
                onFieldNameClick={props.onFieldNameClick}
                onFieldGroupNameClick={props.onFieldGroupNameClick}
                onSortEnd={props.onSortEnd}
                fieldTableActions={props.fieldTableActions}
                fieldGroupTableActions={props.fieldGroupTableActions}
              />
            </>
          </Collapse.Panel>
        </Collapse>
      </td>
    </tr>
  );
}

function FieldTr(props: { field: Field; level: string[]; tableActions?: any; onFieldNameClick: (field: Field, level?: string[]) => void }) {
  const { Text } = Typography;
  const dataTypeName = getDataTypeName(props.field.dataType);

  const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);

  return (
    <tr className="field">
      <td>
        <div style={{ paddingLeft: '2.5rem' }}>
          <Space>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', border: 0, fontWeight: 600 }}
              onClick={() => props.onFieldNameClick(props.field, props.level)}
            >
              {props.field.displayName || props.field.name} {props.field.displayName ? `(${props.field.name})` : ''}
            </Button>
            <Tooltip title={parseHyperlink(props.field.description)}>
              <Text type="secondary">
                <InfoCircleOutlined />
              </Text>
            </Tooltip>
          </Space>
        </div>
        <div style={{ paddingLeft: '2.5rem' }}>
          <Space style={{ fontSize: '0.7rem' }}>
            <Text type="secondary">
              <b>Belirteç:</b> {props.field.isIdentifier ? 'Evet' : 'Hayır'}
            </Text>
            <Text type="secondary">
              <b>Önem:</b> {props.field.priority === 3 ? '!!!' : props.field.priority === 2 ? '!!' : '!'}
            </Text>
            <Text type="secondary">
              <b>Görev otomatik onay:</b> {props.field.autoApproveValueTasks ? 'Evet' : 'Hayır'}
            </Text>
            <Text type="secondary">
              <b>Görev otomatik onay (ilk aktarım):</b> {props.field.autoApproveValueTasksOnCreate ? 'Evet' : 'Hayır'}
            </Text>
            <Text type="secondary">
              <b>Veri tipi:</b> {dataTypeName}
            </Text>
            <Text type="secondary">
              <b>Zorunlu:</b> {props.field.isRequired ? 'Evet' : 'Hayır'}
            </Text>
            <Text type="secondary">
              <b>Tekil:</b> {props.field.isUnique ? 'Evet' : 'Hayır'}
            </Text>
            <Text type="secondary">
              <b>Önceki revizyonu kopyala:</b> {props.field.copyPreviousRevisionValue ? 'Evet' : 'Hayır'}
            </Text>
            <Text type="secondary">
              <b>Kopyalama işlemine dahil et:</b> {props.field.includeInCopy ? 'Evet' : 'Hayır'}
            </Text>
            {props.field.rule?.length > 0 && (
              <Text type="secondary">
                <b>Kural </b> ({props.field.rule?.length})
              </Text>
            )}
            {props.field.minValue && (
              <Text type="secondary">
                <b>En az:</b> {props.field.minValue}
              </Text>
            )}
            {props.field.minValueField && (
              <Text type="secondary">
                <b>En az:</b> {props.field.minValueField.displayName || props.field.minValueField.name}
              </Text>
            )}
            {props.field.maxValue && (
              <Text type="secondary">
                <b>En çok:</b> {props.field.maxValue}
              </Text>
            )}
            {props.field.maxValueField && (
              <Text type="secondary">
                <b>En çok:</b> {props.field.maxValueField.displayName || props.field.maxValueField.name}
              </Text>
            )}
          </Space>
        </div>
      </td>
      <td>
        <div style={{ textAlign: 'right', paddingRight: '1rem' }}>
          <Space>
            <Dropdown overlay={props.tableActions(props.field.id, props.level)} trigger={['click']}>
              <Button size="small" icon={<EllipsisOutlined />} />
            </Dropdown>
            <DragHandle />
          </Space>
        </div>
      </td>
    </tr>
  );
}
